import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  ChangePasswordRequest,
  Patient,
  PatientsResponse,
  PatientStats,
  Visit,
  VisitsResponse,
  VisitStats,
  PatientVisitsResponse,
  Medicine,
  MedicineStats,
  Reference,
  ReferenceStats,
  FinancialOverview,
  PatientAccountSummary,
  PatientFormData,
  VisitFormData,
  MedicineFormData,
  ReferenceFormData,
  PatientFilters,
  VisitFilters,
  MedicineFilters,
  ReferenceFilters,
  User
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Load token from localStorage
    this.token = localStorage.getItem('token');
    if (this.token) {
      this.setAuthToken(this.token);
    }

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          this.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  private setAuthToken(token: string) {
    this.token = token;
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token);
  }

  private clearAuthToken() {
    this.token = null;
    delete this.api.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await this.api.post('/auth/login', credentials);
    if (response.data.success && response.data.data) {
      this.setAuthToken(response.data.data.token);
    }
    return response.data;
  }

  async logout(): Promise<ApiResponse> {
    try {
      await this.api.post('/auth/logout');
    } catch (error) {
      // Ignore logout errors
    } finally {
      this.clearAuthToken();
    }
    return { success: true, message: 'Logged out successfully' };
  }

  async getProfile(): Promise<ApiResponse<{ user: User }>> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.get('/auth/profile');
    return response.data;
  }

  async changePassword(data: ChangePasswordRequest): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.put('/auth/change-password', data);
    return response.data;
  }

  async updateProfile(data: { name: string; username: string }): Promise<ApiResponse<{ user: User }>> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.put('/auth/profile', data);
    return response.data;
  }

  async uploadProfilePicture(file: File): Promise<ApiResponse<{ profile_picture: string }>> {
    const formData = new FormData();
    formData.append('profile_picture', file);

    const response: AxiosResponse<ApiResponse<{ profile_picture: string }>> = await this.api.post('/auth/profile/picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteProfilePicture(): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete('/auth/profile/picture');
    return response.data;
  }

  // Patients
  async getPatients(filters: PatientFilters = {}): Promise<ApiResponse<PatientsResponse>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response: AxiosResponse<ApiResponse<PatientsResponse>> = await this.api.get(`/patients?${params}`);
    return response.data;
  }

  async getPatient(id: number): Promise<ApiResponse<{ patient: Patient }>> {
    const response: AxiosResponse<ApiResponse<{ patient: Patient }>> = await this.api.get(`/patients/${id}`);
    return response.data;
  }

  async getFollowUpPatients(): Promise<ApiResponse<Patient[]>> {
    const response: AxiosResponse<ApiResponse<Patient[]>> = await this.api.get('/patients/followup');
    return response.data;
  }

  async createPatient(data: PatientFormData): Promise<ApiResponse<{ patient: Patient }>> {
    const response: AxiosResponse<ApiResponse<{ patient: Patient }>> = await this.api.post('/patients', data);
    return response.data;
  }

  async updatePatient(id: number, data: PatientFormData): Promise<ApiResponse<{ patient: Patient }>> {
    const response: AxiosResponse<ApiResponse<{ patient: Patient }>> = await this.api.put(`/patients/${id}`, data);
    return response.data;
  }

  async deletePatient(id: number): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/patients/${id}`);
    return response.data;
  }

  async getPatientStats(filters: { dateFrom?: string; dateTo?: string } = {}): Promise<ApiResponse<PatientStats>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
    const response: AxiosResponse<ApiResponse<PatientStats>> = await this.api.get(`/patients/stats/overview?${params}`);
    return response.data;
  }

  async getMonthlyPatientStats(month?: string, year?: string): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (year) params.append('year', year);
    
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/patients/stats/monthly?${params}`);
    return response.data;
  }

  // Visits
  async getVisits(filters: VisitFilters = {}): Promise<ApiResponse<VisitsResponse>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response: AxiosResponse<ApiResponse<VisitsResponse>> = await this.api.get(`/visits?${params}`);
    return response.data;
  }

  async getVisit(id: number): Promise<ApiResponse<{ visit: Visit }>> {
    const response: AxiosResponse<ApiResponse<{ visit: Visit }>> = await this.api.get(`/visits/${id}`);
    return response.data;
  }

  async createVisit(data: VisitFormData | FormData): Promise<ApiResponse<{ visit: Visit }>> {
    const isFormData = data instanceof FormData;
    const config = isFormData ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};
    
    const response: AxiosResponse<ApiResponse<{ visit: Visit }>> = await this.api.post('/visits', data, config);
    return response.data;
  }

  async updateVisit(id: number, data: VisitFormData | FormData): Promise<ApiResponse<{ visit: Visit }>> {
    const isFormData = data instanceof FormData;
    const config = isFormData ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};
    
    const response: AxiosResponse<ApiResponse<{ visit: Visit }>> = await this.api.put(`/visits/${id}`, data, config);
    return response.data;
  }

  async deleteVisit(id: number): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/visits/${id}`);
    return response.data;
  }

  async getPatientVisits(patientId: number, page = 1, limit = 10): Promise<ApiResponse<PatientVisitsResponse>> {
    const response: AxiosResponse<ApiResponse<PatientVisitsResponse>> = await this.api.get(
      `/visits/patient/${patientId}?page=${page}&limit=${limit}`
    );
    return response.data;
  }

  async getVisitStats(dateFrom?: string, dateTo?: string): Promise<ApiResponse<VisitStats>> {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);

    const response: AxiosResponse<ApiResponse<VisitStats>> = await this.api.get(`/visits/stats/overview?${params}`);
    return response.data;
  }

  async getMonthlyVisitStats(month?: string, year?: string): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (year) params.append('year', year);
    
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/visits/stats/monthly?${params}`);
    return response.data;
  }

  // Medicines
  async getMedicines(filters: MedicineFilters = {}): Promise<ApiResponse<{ medicines: Medicine[] }>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response: AxiosResponse<ApiResponse<{ medicines: Medicine[] }>> = await this.api.get(`/medicines?${params}`);
    return response.data;
  }

  async getMedicine(id: number): Promise<ApiResponse<{ medicine: Medicine }>> {
    const response: AxiosResponse<ApiResponse<{ medicine: Medicine }>> = await this.api.get(`/medicines/${id}`);
    return response.data;
  }

  async createMedicine(data: MedicineFormData): Promise<ApiResponse<{ medicine: Medicine }>> {
    const response: AxiosResponse<ApiResponse<{ medicine: Medicine }>> = await this.api.post('/medicines', data);
    return response.data;
  }

  async updateMedicine(id: number, data: MedicineFormData): Promise<ApiResponse<{ medicine: Medicine }>> {
    const response: AxiosResponse<ApiResponse<{ medicine: Medicine }>> = await this.api.put(`/medicines/${id}`, data);
    return response.data;
  }

  async toggleMedicine(id: number): Promise<ApiResponse<{ medicine: Medicine }>> {
    const response: AxiosResponse<ApiResponse<{ medicine: Medicine }>> = await this.api.patch(`/medicines/${id}/toggle`);
    return response.data;
  }

  async deleteMedicine(id: number): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/medicines/${id}`);
    return response.data;
  }

  async searchMedicines(query: string): Promise<ApiResponse<{ medicines: Medicine[] }>> {
    const response: AxiosResponse<ApiResponse<{ medicines: Medicine[] }>> = await this.api.get(`/medicines/search/${query}`);
    return response.data;
  }

  async getMedicineStats(): Promise<ApiResponse<MedicineStats>> {
    const response: AxiosResponse<ApiResponse<MedicineStats>> = await this.api.get('/medicines/stats/overview');
    return response.data;
  }

  async updateMedicineStatus(id: number, IsActive: number): Promise<ApiResponse<{ medicine: Medicine }>> {
    const response: AxiosResponse<ApiResponse<{ medicine: Medicine }>> = await this.api.patch(`/medicines/${id}`, { IsActive });
    return response.data;
  }

  // References
  async getReferences(filters: ReferenceFilters = {}): Promise<ApiResponse<{ references: Reference[] }>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response: AxiosResponse<ApiResponse<{ references: Reference[] }>> = await this.api.get(`/references?${params}`);
    return response.data;
  }

  async getReference(id: number): Promise<ApiResponse<{ reference: Reference }>> {
    const response: AxiosResponse<ApiResponse<{ reference: Reference }>> = await this.api.get(`/references/${id}`);
    return response.data;
  }

  async createReference(data: ReferenceFormData): Promise<ApiResponse<{ reference: Reference }>> {
    const response: AxiosResponse<ApiResponse<{ reference: Reference }>> = await this.api.post('/references', data);
    return response.data;
  }

  async updateReference(id: number, data: ReferenceFormData): Promise<ApiResponse<{ reference: Reference }>> {
    const response: AxiosResponse<ApiResponse<{ reference: Reference }>> = await this.api.put(`/references/${id}`, data);
    return response.data;
  }

  async toggleReference(id: number): Promise<ApiResponse<{ reference: Reference }>> {
    const response: AxiosResponse<ApiResponse<{ reference: Reference }>> = await this.api.patch(`/references/${id}/toggle`);
    return response.data;
  }

  async deleteReference(id: number): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/references/${id}`);
    return response.data;
  }

  async getReferencesByType(type: string): Promise<ApiResponse<{ references: Reference[] }>> {
    const response: AxiosResponse<ApiResponse<{ references: Reference[] }>> = await this.api.get(`/references/type/${type}`);
    return response.data;
  }

  async searchReferences(query: string): Promise<ApiResponse<{ references: Reference[] }>> {
    const response: AxiosResponse<ApiResponse<{ references: Reference[] }>> = await this.api.get(`/references/search/${query}`);
    return response.data;
  }

  async getReferenceTypes(): Promise<ApiResponse<{ types: string[] }>> {
    const response: AxiosResponse<ApiResponse<{ types: string[] }>> = await this.api.get('/references/types/list');
    return response.data;
  }

  async getReferenceStats(): Promise<ApiResponse<ReferenceStats>> {
    const response: AxiosResponse<ApiResponse<ReferenceStats>> = await this.api.get('/references/stats/overview');
    return response.data;
  }

  // Accounts
  async getFinancialOverview(dateFrom?: string, dateTo?: string): Promise<ApiResponse<FinancialOverview>> {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);

    const response: AxiosResponse<ApiResponse<FinancialOverview>> = await this.api.get(`/accounts/overview?${params}`);
    return response.data;
  }

  async getDatewiseAccounts(filters: { page?: number; limit?: number; dateFrom?: string; dateTo?: string } = {}): Promise<ApiResponse<{ datewiseAccounts: any[]; paymentModes: string[]; pagination: any }>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response: AxiosResponse<ApiResponse<{ datewiseAccounts: any[]; paymentModes: string[]; pagination: any }>> = await this.api.get(`/accounts/datewise?${params}`);
    return response.data;
  }
  async getTransactions(filters: { page?: number; limit?: number; dateFrom?: string; dateTo?: string; hasPendingBalance?: string; hasCredit?: string } = {}): Promise<ApiResponse<{ transactions: any[]; pagination: any }>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response: AxiosResponse<ApiResponse<{ transactions: any[]; pagination: any }>> = await this.api.get(`/accounts/transactions?${params}`);
    return response.data;
  }

  async getPatientAccount(patientId: number): Promise<ApiResponse<PatientAccountSummary>> {
    const response: AxiosResponse<ApiResponse<PatientAccountSummary>> = await this.api.get(`/accounts/patient/${patientId}`);
    return response.data;
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!this.token;
  }

  getToken(): string | null {
    return this.token;
  }

  // Construct full URL for attachments
  getAttachmentUrl(attachmentPath: string): string {
    if (!attachmentPath) return '';
    if (attachmentPath.startsWith('http')) return attachmentPath;
    let baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    // Remove trailing /api if present
    baseURL = baseURL.replace(/\/api$/, '');
    const fullUrl = `${baseURL}${attachmentPath}`;
    console.log('Attachment URL construction:', {
      attachmentPath,
      baseURL,
      fullUrl,
      env: process.env.REACT_APP_API_URL
    });
    return fullUrl;
  }
}

export const apiService = new ApiService();
export default apiService;